import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useDesignSystem } from '../../context/DesignSystemContext';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { messageI18n } from './i18n';
import blockedClientImg from './assets/blocked-client.svg';
import './Message.scss';

const BUILTIN_IMAGES = {
  blocked: blockedClientImg,
  blockedClient: blockedClientImg,
};

const directionClassMap = {
  horizontal: 'horizontal',
  vertical: 'vertical',
  reverse: 'reverse',
};

const typeClassMap = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  error: 'danger',
  neutral: 'neutral',
};

export default function Message({
  type = 'neutral',
  direction = 'horizontal',
  image = 'blocked',
  illustrationAlt,
  title,
  description,
  children,
  className = '',
  floating = false,
  ...rest
}) {
  const { t } = useScopedTranslation(messageI18n);
  const designSystem = useDesignSystem();
  const Card = designSystem?.Card;

  const resolvedDirection =
    directionClassMap[direction] ?? directionClassMap.horizontal;
  const resolvedType = typeClassMap[type] ?? typeClassMap.neutral;

  const resolvedImgSource = useMemo(() => {
    if (!image) return null;
    if (typeof image === 'string') {
      return BUILTIN_IMAGES[image] || image;
    }
    return image;
  }, [image]);

  const Wrapper = Card || 'div';
  const wrapperProps = Card ? { specialPadding: 'p-4' } : {};

  return (
    <Wrapper
      className={[
        'ds-message-card',
        `ds-message-card--${resolvedDirection}`,
        `ds-message-card--${resolvedType}`,
        floating ? 'ds-message-card--floating' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...wrapperProps}
      {...rest}
    >
      {resolvedImgSource && (
        <div className='ds-message-card__image-wrapper'>
          {typeof resolvedImgSource === 'string' ? (
            <img
              src={resolvedImgSource}
              alt={illustrationAlt || title || t('message_illustration_alt')}
              className='ds-message-card__image'
            />
          ) : (
            resolvedImgSource
          )}
        </div>
      )}

      <div className='ds-message-card__content'>
        {title && <h3 className='ds-message-card__title'>{title}</h3>}
        {description && (
          <div className='ds-message-card__description'>{description}</div>
        )}
        {children && (
          <div className='ds-message-card__extra'>{children}</div>
        )}
      </div>
    </Wrapper>
  );
}

Message.propTypes = {
  type: PropTypes.oneOf(['neutral', 'info', 'success', 'warning', 'danger', 'error']),
  direction: PropTypes.oneOf(['horizontal', 'vertical', 'reverse']),
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  illustrationAlt: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node,
  className: PropTypes.string,
  floating: PropTypes.bool,
};
